import { useKeyboardArrowNavigation } from '~/src/common/hooks/useKeyboardArrowNavigation';
import { useMobileSwipeNavigation } from '~/src/common/hooks/useMobileSwipeNavigation';
import { useNthChildrenPosition } from '~/src/common/hooks/useNthChildrenPosition';
import { useNiceModal } from '~/src/common/services/ModalsManager';

import { useSliderIndex } from './hooks/useSliderIndex';
import * as S from './layout';
import { ImageSliderEntry } from './types';

interface Props {
  title: string;
  images: ImageSliderEntry[];
  className?: string;
  hideThumbnails?: boolean;
  onArrowClick?: (direction: 'left' | 'right') => void;
  onThumbnailClick?: (source: 'photo modal' | 'product page') => void;
}

const ImageSlider = (props: Props) => {
  const { onArrowClick, onThumbnailClick } = props;
  const { title, images, className, hideThumbnails } = props;

  // Gestion de l'index en cours
  const count = images.length;
  const { index, goto, prev, next } = useSliderIndex(count);

  // Position de l'image active pour déplacement du parent
  const { parentRef, position } = useNthChildrenPosition(index);
  const parentStyle = position ? { translate: -position.left } : undefined;

  // Navigation via les flèches du clavier et le swipe sur mobile
  useKeyboardArrowNavigation({ onPressLeft: prev, onPressRight: next });
  const swipeEvents = useMobileSwipeNavigation({ onSwipeRight: prev, onSwipeLeft: next });

  // Navigation via les flèches du carousel et les miniatures
  const onLeftArrow = () => void (prev(), onArrowClick?.('left'));
  const onRightArrow = () => void (next(), onArrowClick?.('right'));
  const onThumbClick = (idx: number) => void (goto(idx), onThumbnailClick?.('product page'));

  // Gestion de l'affichage de la modale de zoom
  const { show } = useNiceModal('image-zoom-modal');
  const showZoomModal = (idx: number) => {
    const onClick = () => onThumbnailClick?.('photo modal');
    void show({ title, images, initialIndex: idx, hideThumbnails, onThumbnailClick: onClick });
  };

  return (
    <S.Container className={className} {...swipeEvents}>
      {hideThumbnails ? null : (
        <S.Thumbnails>
          {images.map((img, idx) => (
            <img
              key={img.id}
              src={img.src.s}
              alt={img.alt ?? `${title} - ${idx + 1}`}
              aria-current={idx === index}
              onClick={() => onThumbClick(idx)}
            />
          ))}
        </S.Thumbnails>
      )}
      <S.Carousel>
        <S.Images ref={parentRef} style={parentStyle}>
          {images.map((img, idx) => (
            <S.Image key={img.id} aria-current={idx === index} onClick={() => showZoomModal(idx)}>
              <img src={img.src.m} alt={img.alt ?? `${title} - ${idx + 1}`} />
            </S.Image>
          ))}
        </S.Images>
        {count > 1 ? (
          <>
            <S.Arrow variant="secondary" icon="chevron-left" onClick={onLeftArrow} data-prev />
            <S.Arrow variant="secondary" icon="chevron-right" onClick={onRightArrow} data-next />
          </>
        ) : null}
      </S.Carousel>
    </S.Container>
  );
};

export default ImageSlider;
